import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanies } from "../hooks/useCompanies";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherArrowLeft,
  FeatherBuilding,
  FeatherRefreshCw,
} from "@subframe/core";
import {
  listTenantConfigs,
  TenantConfigDTO,
} from "../../data/api/provisioningApi";
import {
  COUNTRIES,
  INDIAN_STATES,
  isIndia,
} from "../../../../shared/utils/locationUtils";
import * as SubframeUtils from "../../../../ui/utils";

// New tenants live on the unified deployment at {tenant}.initium.zournalhq.com.
// Kept in sync with the backend default (CompanyUrlHelper::tenantBaseDomain()).
const TENANT_BASE_DOMAIN = "initium.zournalhq.com";

const buildZohoRedirectUri = (subdomainPrefix: string) => {
  const prefix = subdomainPrefix
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .replace(/\.initium\.zournalhq\.com$/i, "")
    .replace(/\.app\.zournalhq\.com$/i, "")
    .replace(/\.zournalhq$/i, "")
    .replace(/\.zournalhq\.com$/i, "")
    .split(".")[0]
    .replace(/^\.+|\.+$/g, "");

  return prefix
    ? `https://${prefix}.${TENANT_BASE_DOMAIN}/zoho-callback`
    : "";
};

export const CompanyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createCompany, loading, error } = useCompanies();
  const [formData, setFormData] = useState({
    company_name: "",
    app_url: "",
    zoho_org_id: "",
    zoho_client_id: "",
    zoho_client_secret: "",
    zoho_redirect_uri: "",
    address_line1: "",
    address_line2: "",
    state: "",
    country: "",
    pincode: "",
    gst_number: "",
    pan_number: "",
    cin_number: "",
    currency: "INR",
    msme: 0,
    company_gst_enabled: 0,
    vendor_type: "vendor_lite" as "vendor_lite" | "vendor_pro",
    cashfree_client_id: "",
    cashfree_client_secret: "",
    gstin_verification_required: "Y" as "Y" | "N",
    dso_lookback_months: 6,
    accounting_start_month: "",
    amount_compact_unit: "lakhs" as "lakhs" | "crores",
    amount_compact_decimals: 2,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [adminCredentials, setAdminCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);

  // Provisioned tenants awaiting a company (status 'ready', unbound) — the
  // only valid subdomains for a new company, so they're offered as a dropdown.
  const [readyTenants, setReadyTenants] = useState<TenantConfigDTO[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState<string | null>(null);

  const fetchReadyTenants = useCallback(async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      const res = await listTenantConfigs();
      const ready = (res.data?.configs ?? []).filter(
        (cfg) => cfg.status === "ready" && !cfg.company_id,
      );
      setReadyTenants(ready);
    } catch (err: any) {
      setTenantsError(err.message || "Failed to load provisioned tenants");
    } finally {
      setTenantsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReadyTenants();
  }, [fetchReadyTenants]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;

    // Clear state when country changes and it's not India
    if (name === "country" && !isIndia(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        state: "",
      }));
    } else {
      const updatedData: any = {
        ...formData,
        [name]:
          type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : type === "number"
              ? parseInt(value) || 0
              : value,
      };

      // Auto-generate zoho_redirect_uri when app_url changes
      if (name === "app_url") {
        updatedData.zoho_redirect_uri = buildZohoRedirectUri(value);
      }

      setFormData(updatedData);
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.company_name.trim()) {
      newErrors.company_name = "Company name is required";
    }
    if (!formData.app_url.trim()) {
      newErrors.app_url = "Select a provisioned tenant";
    }
    if (formData.company_gst_enabled === 1 && !formData.gst_number.trim()) {
      newErrors.gst_number = "GST number is required when GST is enabled";
    }

    if (
      formData.accounting_start_month &&
      !/^\d{4}-(0[1-9]|1[0-2])$/.test(formData.accounting_start_month)
    ) {
      newErrors.accounting_start_month =
        "Accounting start month must be YYYY-MM (e.g. 2024-04)";
    }

    if (formData.dso_lookback_months < 1) {
      newErrors.dso_lookback_months = "DSO lookback months must be at least 1";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const result = await createCompany({
        ...formData,
        msme: formData.msme ? 1 : 0,
        company_gst_enabled: formData.company_gst_enabled ? 1 : 0,
        dso_lookback_months: Number(formData.dso_lookback_months),
        amount_compact_decimals: Number(formData.amount_compact_decimals),
        accounting_start_month: formData.accounting_start_month.trim() || null,
        cashfree_client_id: formData.cashfree_client_id.trim() || undefined,
        cashfree_client_secret:
          formData.cashfree_client_secret.trim() || undefined,
      });
      setAdminCredentials(result.admin_credentials);
    } catch (err: any) {
      alert(err.message || "Failed to create company");
    }
  };

  if (adminCredentials) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col gap-6 p-12">
          <div className="flex w-full items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/admin/dashboard?tab=companies")}
            >
              Back
            </Button>
            <span className="text-heading-2 font-heading-2 text-default-font">
              Company Created Successfully
            </span>
          </div>

          <div className="flex w-full flex-col items-center justify-center">
            <div className="flex w-full max-w-2xl flex-col items-start gap-4 rounded-lg border border-solid border-green-200 bg-green-50 p-6">
              <span className="text-heading-3 font-heading-3 text-green-800">
                Company Admin Credentials
              </span>
              <div className="flex flex-col gap-2">
                <div>
                  <span className="text-body font-body text-green-700 font-semibold">
                    Email:{" "}
                  </span>
                  <span className="text-body font-body text-green-800">
                    {adminCredentials.email}
                  </span>
                </div>
                <div>
                  <span className="text-body font-body text-green-700 font-semibold">
                    Password:{" "}
                  </span>
                  <span className="text-body font-body text-green-800">
                    {adminCredentials.password}
                  </span>
                </div>
              </div>
              <p className="text-sm text-green-700 mt-4">
                Please save these credentials. The company admin will need to
                change their password on first login.
              </p>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col gap-6 p-12">
        <div className="flex w-full items-center gap-4">
          <Button
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/admin/dashboard?tab=companies")}
          >
            Back
          </Button>
          <span className="text-heading-2 font-heading-2 text-default-font">
            Create Company
          </span>
        </div>

        <div className="flex w-full flex-col items-center justify-center">
          {error && (
            <div className="flex w-full max-w-2xl flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <span className="text-sm font-medium text-red-800">
                {error.message}
              </span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-2xl flex-col items-start gap-6"
          >
            <div className="flex w-full flex-col gap-4">
              <TextField
                label="Company Name"
                helpText={errors.company_name || ""}
                error={!!errors.company_name}
                icon={<FeatherBuilding />}
              >
                <TextField.Input
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Subdomain (provisioned tenant)"
                helpText={
                  errors.app_url ||
                  (formData.app_url
                    ? `Company will be served at https://${formData.app_url}.${TENANT_BASE_DOMAIN}`
                    : "Pick a provisioned tenant that is awaiting a company.")
                }
                error={!!errors.app_url}
              >
                <select
                  name="app_url"
                  value={formData.app_url}
                  onChange={handleChange}
                  disabled={loading || tenantsLoading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="">
                    {tenantsLoading
                      ? "Loading provisioned tenants…"
                      : "Select a provisioned tenant"}
                  </option>
                  {readyTenants.map((cfg) => (
                    <option key={cfg.id} value={cfg.tenant_id}>
                      {cfg.tenant_id} — {cfg.db_name} / {cfg.s3_bucket}
                    </option>
                  ))}
                </select>
              </TextField>

              <div className="flex w-full items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">
                  {tenantsLoading
                    ? "Loading provisioned tenants…"
                    : `${readyTenants.length} provisioned tenant${readyTenants.length === 1 ? "" : "s"} awaiting a company`}
                </span>
                <Button
                  type="button"
                  variant="neutral-tertiary"
                  size="small"
                  icon={<FeatherRefreshCw />}
                  onClick={fetchReadyTenants}
                  disabled={tenantsLoading}
                >
                  Refresh
                </Button>
              </div>

              {tenantsError && (
                <div className="flex w-full items-center justify-between gap-4 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
                  <span className="text-sm font-medium text-red-800">
                    {tenantsError}
                  </span>
                  <Button
                    type="button"
                    variant="neutral-secondary"
                    size="small"
                    onClick={fetchReadyTenants}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {!tenantsLoading && !tenantsError && readyTenants.length === 0 && (
                <div className="flex w-full items-center justify-between gap-4 rounded-lg border border-solid border-amber-200 bg-amber-50 p-4">
                  <span className="text-sm font-medium text-amber-800">
                    No provisioned tenants are awaiting a company. Provision
                    the tenant (DB + S3 credentials) first.
                  </span>
                  <Button
                    type="button"
                    variant="neutral-secondary"
                    size="small"
                    onClick={() =>
                      navigate("/admin/dashboard?tab=provisioning")
                    }
                  >
                    Go to Provisioning
                  </Button>
                </div>
              )}

              <TextField label="Zoho Org ID" helpText="">
                <TextField.Input
                  name="zoho_org_id"
                  value={formData.zoho_org_id}
                  onChange={handleChange}
                  placeholder="Enter Zoho Org ID"
                  disabled={loading}
                />
              </TextField>

              <TextField label="Zoho Client ID" helpText="">
                <TextField.Input
                  name="zoho_client_id"
                  value={formData.zoho_client_id}
                  onChange={handleChange}
                  placeholder="Enter Zoho Client ID"
                  disabled={loading}
                />
              </TextField>

              <TextField label="Zoho Client Secret" helpText="">
                <TextField.Input
                  name="zoho_client_secret"
                  type="password"
                  value={formData.zoho_client_secret}
                  onChange={handleChange}
                  placeholder="Enter Zoho Client Secret"
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Zoho Redirect URI"
                helpText="Auto-generated from subdomain. Format: https://{subdomain}.zournalhq.com/zoho-callback"
              >
                <TextField.Input
                  name="zoho_redirect_uri"
                  value={formData.zoho_redirect_uri}
                  onChange={handleChange}
                  placeholder="Auto-generated from subdomain"
                  disabled={loading}
                />
              </TextField>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="company_gst_enabled"
                  checked={formData.company_gst_enabled === 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      company_gst_enabled: e.target.checked ? 1 : 0,
                    }))
                  }
                  className="h-4 w-4"
                />
                <label className="text-body font-body text-default-font">
                  Company GST Enabled
                </label>
              </div>

              <div className="flex flex-col items-start gap-3">
                <label className="text-body font-body text-default-font font-semibold">
                  Vendor Type
                </label>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="vendor_type"
                      value="vendor_lite"
                      checked={formData.vendor_type === "vendor_lite"}
                      onChange={handleChange}
                      className="h-4 w-4"
                      disabled={loading}
                    />
                    <label className="text-body font-body text-default-font">
                      Vendor Lite
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="vendor_type"
                      value="vendor_pro"
                      checked={formData.vendor_type === "vendor_pro"}
                      onChange={handleChange}
                      className="h-4 w-4"
                      disabled={loading}
                    />
                    <label className="text-body font-body text-default-font">
                      Vendor Pro
                    </label>
                  </div>
                </div>
              </div>

              {formData.company_gst_enabled === 1 && (
                <TextField
                  label="GST Number"
                  helpText={errors.gst_number || ""}
                  error={!!errors.gst_number}
                >
                  <TextField.Input
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    placeholder="Enter GST number"
                    disabled={loading}
                  />
                </TextField>
              )}

              <TextField label="PAN Number">
                <TextField.Input
                  name="pan_number"
                  value={formData.pan_number}
                  onChange={handleChange}
                  placeholder="Enter PAN number"
                  disabled={loading}
                />
              </TextField>

              <TextField label="Currency" helpText="ISO currency code (e.g. INR)">
                <TextField.Input
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  placeholder="INR"
                  disabled={loading}
                />
              </TextField>

              <TextField label="Cashfree Client ID" helpText="">
                <TextField.Input
                  name="cashfree_client_id"
                  value={formData.cashfree_client_id}
                  onChange={handleChange}
                  placeholder="Enter Cashfree client ID"
                  disabled={loading}
                />
              </TextField>

              <TextField label="Cashfree Client Secret" helpText="">
                <TextField.Input
                  name="cashfree_client_secret"
                  type="password"
                  value={formData.cashfree_client_secret}
                  onChange={handleChange}
                  placeholder="Enter Cashfree client secret"
                  disabled={loading}
                />
              </TextField>

              <TextField label="GSTIN Verification Required" helpText="">
                <select
                  name="gstin_verification_required"
                  value={formData.gstin_verification_required}
                  onChange={handleChange}
                  disabled={loading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </TextField>

              <TextField
                label="DSO Lookback Months"
                helpText={errors.dso_lookback_months || "Rolling window for DSO averaging"}
                error={!!errors.dso_lookback_months}
              >
                <TextField.Input
                  name="dso_lookback_months"
                  type="number"
                  min={1}
                  value={String(formData.dso_lookback_months)}
                  onChange={handleChange}
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Accounting Start Month"
                helpText={
                  errors.accounting_start_month ||
                  "First books month (YYYY-MM). Leave empty if not set."
                }
                error={!!errors.accounting_start_month}
              >
                <input
                  name="accounting_start_month"
                  type="month"
                  value={formData.accounting_start_month}
                  onChange={handleChange}
                  disabled={loading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                />
              </TextField>

              <TextField label="Amount Compact Unit" helpText="">
                <select
                  name="amount_compact_unit"
                  value={formData.amount_compact_unit}
                  onChange={handleChange}
                  disabled={loading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="lakhs">Lakhs</option>
                  <option value="crores">Crores</option>
                </select>
              </TextField>

              <TextField label="Amount Compact Decimals" helpText="">
                <select
                  name="amount_compact_decimals"
                  value={String(formData.amount_compact_decimals)}
                  onChange={handleChange}
                  disabled={loading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </TextField>

              <TextField label="Country">
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={loading}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="">Select Country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </TextField>

              <TextField label="State">
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={loading || !isIndia(formData.country)}
                  className={SubframeUtils.twClassNames(
                    "h-full w-full border-none bg-transparent text-body font-body text-default-font outline-none",
                  )}
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </TextField>

              <TextField label="Address Line">
                <TextField.Input
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  placeholder="Enter address"
                  disabled={loading}
                />
              </TextField>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  className="w-auto"
                >
                  Create Company
                </Button>
                <Button
                  type="button"
                  variant="neutral-secondary"
                  onClick={() => navigate("/admin/dashboard?tab=companies")}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
